import { HttpClient, PaginatedResponse, robotoHeaders } from "../../http";
import { MetadataChangeset } from "../metadataChangeset";
import { Token } from "../tokens";

import { type Device } from "./Device";

interface Options {
  abortSignal: AbortSignal;
  resourceOwnerId: string;
  searchParams: URLSearchParams;
  pageToken?: string;
}

interface UpdateDeviceRequest {
  metadata_changeset?: MetadataChangeset;
}

export class DeviceService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async getDevicesByOrg(
    orgId: string,
    options?: Partial<Options>,
  ): Promise<PaginatedResponse<Device>> {
    const searchParams = new URLSearchParams();

    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/devices/org/${orgId}`,
      searchParams,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<PaginatedResponse<Device>>();
  }

  /**
   * Fetches all devices for an organization, handling pagination internally.
   * Returns a flat array of all Device objects.
   */
  public async getAllDevicesByOrg(
    orgId: string,
    options?: Partial<Pick<Options, "abortSignal">>,
  ): Promise<Device[]> {
    const allDevices: Device[] = [];
    let pageToken: string | undefined = undefined;

    while (true) {
      const response = await this.getDevicesByOrg(orgId, {
        abortSignal: options?.abortSignal,
        pageToken,
      });

      allDevices.push(...response.items);

      if (response.next_token) {
        pageToken = response.next_token;
      } else {
        break;
      }
    }

    return allDevices;
  }

  public async getDevice(
    deviceId: string,
    options?: Partial<Options>,
  ): Promise<Device> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/devices/id/${deviceId}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return await response.json<Device>();
  }

  public async getTokensForDevice(
    deviceId: string,
    options?: Partial<Options>,
  ): Promise<Token[]> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/devices/id/${deviceId}/tokens`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return await response.json<Token[]>();
  }

  /**
   * Preview the devices a set of files implies — the union of the `device_id`
   * recorded on each file and on its parent dataset — without creating a
   * session. The backend applies the same inference when files are attached
   * to a session, so the preview matches what a created session would get.
   * Returns the distinct device ids, unordered.
   *
   * Scoped to the org named by `resourceOwnerId`, which is required (throws
   * when absent, like {@link updateDevice}); files outside that org
   * contribute nothing.
   */
  public async inferDevices(
    fileIds: string[],
    options?: Partial<Pick<Options, "abortSignal" | "resourceOwnerId">>,
  ): Promise<string[]> {
    if (!options?.resourceOwnerId) {
      throw Error("inferDevices requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl("v1/devices/infer");
    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
      body: JSON.stringify({ file_ids: fileIds }),
      signal: options?.abortSignal,
    });
    const body = await response.json<{ device_ids: string[] }>();
    return body.device_ids;
  }

  public async updateDevice(
    deviceId: string,
    updates: UpdateDeviceRequest,
    options?: Partial<Options>,
  ): Promise<Device> {
    if (!options?.resourceOwnerId) {
      throw Error("updateDevice requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/devices/id/${deviceId}`,
    );

    const response = await this.#httpClient.put(requestUrl, {
      idempotent: true,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
      body: JSON.stringify(updates),
    });

    return await response.json<Device>();
  }
}
